import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  InputLabel,
  Link,
  ListItemText,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import AssignmentIcon from "@material-ui/icons/Assignment";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import PeopleIcon from "@material-ui/icons/People";
import { useDispatch, useSelector } from "react-redux";
import { DateTime } from "luxon";
import DateTimeUtils, { FORMAT_TYPE } from "../../utils/DateTimeUtils";
import EnumUtils from "../../utils/EnumUtils";
import ShiftStatus from "../../types/enum/ShiftStatus";
import { CastReportType } from "types/enum/CastReportType";
import { useLocation } from "react-router-dom";
import { fetchStaff, updateStaff } from "redux/actions/staff";
import Select from "@material-ui/core/Select";
import Input from "@material-ui/core/Input";
import Checkbox from "@material-ui/core/Checkbox";
import ChangeLogApi from "api/ChangeLogApi";
import DashboardApi from "api/DashboardApi";
import DayNav from "components/DayNav";
import SystemNotice from "components/SystemNotice";
import CsvDateExport from "components/CsvDateExport";
import PageUtils from "utils/PageUtils";
import CastReportRes from "types/res/castReport/CastReportRes";
import CastShiftRes from "types/res/castShift/CastShiftRes";
import DriverShiftRes from "types/res/driverShift/DriverShiftRes";
import OrderRes from "types/res/order/OrderRes";
import ChangeLogRes from "types/res/changeLog/ChangeLogRes";
import StaffRes from "types/res/staff/StaffRes";
import StaffRole from "types/enum/StaffRole";

const Landing = () => {
  const dispatch = useDispatch();
  const queryString = new URLSearchParams(useLocation().search);
  const queryCompanyId = queryString.get("companyId");
  const companyId = useSelector((state) => state.account.staff.companyId);
  const accessToken = useSelector((state) => state.account.accessToken);
  const staff = useSelector((state) => state.account?.staff);
  const changeDateTime = useSelector(
    (state) => state.account.staff.company.changeDateTime
  );
  const changeDate = changeDateTime
    ? DateTime.fromFormat(changeDateTime, "HH:mm:ss")
    : DateTime.fromFormat("00:00:00", "HH:mm:ss");
  // 営業日基準（changeDateTime を考慮した「本日」）
  const [selectedDate, setSelectedDate] = useState<DateTime>(
    DateTime.local().minus({
      hours: changeDate.hour,
      minutes: changeDate.minute,
    })
  );
  const startDate = selectedDate.toJSDate();
  const endDate = selectedDate.toJSDate();
  const [allChangeLogs, setAllChangeLogs] = useState<ChangeLogRes>();
  const [filteredChangeLog, setFilteredChangeLog] = useState<
    (CastShiftRes | CastReportRes | DriverShiftRes | OrderRes)[]
  >([]);
  const categories = {
    castShift: "キャストシフト",
    driverShift: "ドライバーシフト",
    castReport: "緊急報告",
    order: "受注情報",
  };
  const [selectCategories, setSetSelectCategories] = useState<string[]>([
    ...Object.keys(categories),
    "すべて",
  ]);
  const [selectUpdatedBy, setSelectUpdatedBy] = useState<string[]>([]);
  const [staffList, setStaffList] = useState<StaffRes[]>([]);
  // ダッシュボードサマリー
  const [summary, setSummary] = useState<{
    bookingCount: number;
    bookingSales: number;
    paidCount: number;
    paidSales: number;
    shiftCount: number;
  }>({
    bookingCount: 0,
    bookingSales: 0,
    paidCount: 0,
    paidSales: 0,
    shiftCount: 0,
  });
  // 7日間の出勤予定（日別件数）
  const [weekShifts, setWeekShifts] = useState<
    { date: string; label: string; count: number; isToday: boolean }[]
  >([]);
  // 7日間の予約件数（日別集計）
  const [weekBookings, setWeekBookings] = useState<
    { date: string; label: string; count: number; isToday: boolean }[]
  >([]);
  // 対応が必要（件数）
  const [actionable, setActionable] = useState<{
    castRequest: number;
    guestRequest: number;
  }>({ castRequest: 0, guestRequest: 0 });
  // 本日のキャストシフト一覧
  const [todayShifts, setTodayShifts] = useState<CastShiftRes[]>([]);
  // トライアル状態
  const [trial, setTrial] = useState<{
    isTrialing: boolean;
    trialEnd: Date | null;
    hasPaymentMethod: boolean;
  }>({ isTrialing: false, trialEnd: null, hasPaymentMethod: false });

  useEffect(() => {
    dispatch(fetchStaff(companyId));
  }, [companyId]);

  // サマリー一式を 1 コールで取得（体感ラグ解消のため統合）
  useEffect(() => {
    if (!companyId) return;
    const dateStr = DateTime.fromJSDate(startDate).toFormat(
      FORMAT_TYPE.YEAR_DAY
    );
    const load = async () => {
      try {
        const res = await DashboardApi.summary(companyId, dateStr);
        setSummary({
          bookingCount: res.today.bookingCount,
          bookingSales: res.today.bookingSales,
          paidCount: res.today.paidCount,
          paidSales: res.today.paidSales,
          shiftCount: res.today.shiftCount,
        });
        setActionable(res.actionable);
        // 週バケットにラベル・isToday を付与
        const todayStr = DateTime.local().toFormat("yyyy-MM-dd");
        const decorate = (arr: { date: string; count: number }[]) =>
          arr.map((x) => {
            const d = DateTime.fromFormat(x.date, FORMAT_TYPE.YEAR_DAY);
            return {
              ...x,
              label: `${d.toFormat("M/d")}(${
                ["日", "月", "火", "水", "木", "金", "土"][d.weekday % 7]
              })`,
              isToday: x.date === todayStr,
            };
          });
        setWeekShifts(decorate(res.weekShifts));
        setWeekBookings(decorate(res.weekBookings));
        if ((res as any).trial) {
          const t = (res as any).trial;
          setTrial({
            isTrialing: !!t.isTrialing,
            trialEnd: t.trialEnd ? new Date(t.trialEnd) : null,
            hasPaymentMethod: !!t.hasPaymentMethod,
          });
        }
        setTodayShifts(
          res.todayShifts.map((s) => ({
            ...s,
            planWorkStart: s.planWorkStart
              ? (new Date(s.planWorkStart) as any)
              : null,
            planWorkEnd: s.planWorkEnd ? (new Date(s.planWorkEnd) as any) : null,
            actualWorkStart: s.actualWorkStart
              ? (new Date(s.actualWorkStart) as any)
              : null,
            cast: { castId: s.castId, displayName: s.displayName },
          })) as any
        );
      } catch (e) {
        // 失敗しても変更履歴は表示する
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, selectedDate]);

  useEffect(() => {
    const apiCall = async () => {
      const result = await ChangeLogApi.findAll(
        companyId,
        DateTime.fromJSDate(startDate).toFormat(FORMAT_TYPE.YEAR_DAY),
        DateTime.fromJSDate(endDate).toFormat(FORMAT_TYPE.YEAR_DAY)
      );
      setAllChangeLogs(result);
    };
    apiCall();
    // selectedDate ベースで依存を固定（startDate/endDate は毎レンダで新しい Date 参照になるため）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, selectedDate]);

  useEffect(() => {
    if (!allChangeLogs) return;
    const result = allChangeLogs;
    let items: (CastReportRes | CastShiftRes | DriverShiftRes | OrderRes)[] =
      [];

    if (selectCategories.includes("castShift")) {
      items = [...result.castShift];
    }
    if (selectCategories.includes("driverShift")) {
      items = [...items, ...result.driverShift];
    }
    if (selectCategories.includes("castReport")) {
      items = [...items, ...result.castReport];
    }
    if (selectCategories.includes("order")) {
      items = [...items, ...result.orderInfo];
    }
    items = items.filter((item) => {
      if (item instanceof CastReportRes) {
        return selectUpdatedBy.includes("その他");
      } else {
        return selectUpdatedBy.includes(
          item.updatedById ? String(item.updatedById) : "その他"
        );
      }
    });
    items.sort((a, b) => (a.updatedAt > b.updatedAt ? -1 : 1));
    // 表示件数上限（大量データによるフリーズ防止）
    setFilteredChangeLog(items.slice(0, 200));
  }, [allChangeLogs, selectUpdatedBy, selectCategories]);

  useEffect(() => {
    if (!allChangeLogs) return;
    let list: StaffRes[] = [];
    allChangeLogs.castShift?.forEach((cs) => {
      if (
        cs.updatedBy &&
        !list.find((l) => l.staffId === cs.updatedById) &&
        EnumUtils.mapToEnum(StaffRole, cs.updatedBy.role) !==
          StaffRole.notelMaster
      ) {
        list = [...list, cs.updatedBy];
      }
    });
    allChangeLogs.driverShift?.forEach((cs) => {
      if (
        cs.updatedBy &&
        !list.find((l) => l.staffId === cs.updatedById) &&
        EnumUtils.mapToEnum(StaffRole, cs.updatedBy.role) !==
          StaffRole.notelMaster
      ) {
        list = [...list, cs.updatedBy];
      }
    });
    allChangeLogs.orderInfo?.forEach((cs) => {
      if (
        cs.updatedBy &&
        !list.find((l) => l.staffId === cs.updatedById) &&
        EnumUtils.mapToEnum(StaffRole, cs.updatedBy.role) !==
          StaffRole.notelMaster
      ) {
        list = [...list, cs.updatedBy];
      }
    });
    setStaffList(list);
    setSelectUpdatedBy([
      ...[...list.map((staff) => String(staff.staffId)), "その他"],
      "すべて",
    ]);
  }, [allChangeLogs]);

  useEffect(() => {
    if (!queryCompanyId) return;
    dispatch(
      updateStaff(
        companyId,
        { ...staff, companyId: Number(queryCompanyId) },
        accessToken
      )
    );
  }, [queryCompanyId]);

  // トライアル残日数 (端数切り上げ)
  const trialDaysLeft = trial.trialEnd
    ? Math.ceil(
        (trial.trialEnd.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
      )
    : 0;

  return (
    <Box display="flex" flexDirection="column" padding={2}>
      {/* トライアルバナー */}
      {trial.isTrialing && !trial.hasPaymentMethod && (
        <Box
          p={1.5}
          mb={2}
          borderRadius={8}
          display="flex"
          alignItems="center"
          flexWrap="wrap"
          style={{
            background:
              trialDaysLeft <= 0
                ? "#fdecea"
                : trialDaysLeft <= 3
                ? "#fff4e5"
                : "#e8f4fd",
            border:
              trialDaysLeft <= 0
                ? "1px solid #f44336"
                : trialDaysLeft <= 3
                ? "1px solid #ff9800"
                : "1px solid #2196f3",
            gap: 8,
          }}
        >
          <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
            {trialDaysLeft > 0
              ? `ライトプラン トライアル中 (残り ${trialDaysLeft} 日)`
              : "トライアル期限切れ"}
          </Typography>
          <Typography variant="body2">
            引き続きご利用の場合は、
            <Link href="/payment" style={{ fontWeight: "bold" }}>
              設定 &gt; プラン
            </Link>
            からカード情報のご登録をお願いいたします。
          </Typography>
        </Box>
      )}
      {/* ダッシュボード サマリーカード */}
      <Box
        display="flex"
        flexWrap="wrap"
        style={{ gap: 16, marginBottom: 16 }}
      >
        <Card style={{ flex: "1 1 240px", minWidth: 220 }}>
          <CardContent>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <AssignmentIcon style={{ color: "#1976d2" }} />
              <Typography variant="subtitle2" color="textSecondary">
                本日の受注
              </Typography>
            </Box>
            <Typography
              variant="h5"
              style={{ fontWeight: "bold", marginTop: 8 }}
            >
              予約 {summary.bookingCount} 件 / 完了 {summary.paidCount} 件
            </Typography>
          </CardContent>
        </Card>
        <Card style={{ flex: "1 1 240px", minWidth: 220 }}>
          <CardContent>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <AttachMoneyIcon style={{ color: "#2e7d32" }} />
              <Typography variant="subtitle2" color="textSecondary">
                本日の売上
              </Typography>
            </Box>
            <Typography
              variant="h5"
              style={{ fontWeight: "bold", marginTop: 8 }}
            >
              予約 ¥{summary.bookingSales.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              完了 ¥{summary.paidSales.toLocaleString()}
            </Typography>
          </CardContent>
        </Card>
        <Card style={{ flex: "1 1 240px", minWidth: 220 }}>
          <CardContent>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <PeopleIcon style={{ color: "#ed6c02" }} />
              <Typography variant="subtitle2" color="textSecondary">
                本日のシフト数
              </Typography>
            </Box>
            <Typography
              variant="h5"
              style={{ fontWeight: "bold", marginTop: 8 }}
            >
              {summary.shiftCount} 件
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* 7日間: 出勤予定 + 予約件数 + お知らせ (3カラム) */}
      <Box display="flex" flexWrap="wrap" style={{ gap: 16, marginBottom: 16 }}>
        <Card style={{ flex: "1 1 300px", minWidth: 280 }}>
          <CardContent>
            <Typography
              variant="subtitle1"
              style={{ fontWeight: "bold", marginBottom: 12 }}
            >
              📅 今後7日間の出勤予定
            </Typography>
            <Box
              display="flex"
              flexWrap="wrap"
              style={{ gap: 12, alignItems: "flex-end" }}
            >
              {weekShifts.map((w) => {
                const max = Math.max(...weekShifts.map((x) => x.count), 1);
                const height = (w.count / max) * 80 + 20;
                return (
                  <Box
                    key={w.date}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    style={{ minWidth: 52 }}
                  >
                    <Typography variant="caption" color="textSecondary">
                      {w.count} 件
                    </Typography>
                    <Box
                      style={{
                        width: 32,
                        height,
                        backgroundColor: w.isToday ? "#1976d2" : "#90caf9",
                        borderRadius: "4px 4px 0 0",
                        marginTop: 4,
                      }}
                    />
                    <Typography
                      variant="caption"
                      style={{
                        marginTop: 4,
                        fontWeight: w.isToday ? "bold" : "normal",
                      }}
                    >
                      {w.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </CardContent>
        </Card>
        <Card style={{ flex: "1 1 300px", minWidth: 280 }}>
          <CardContent>
            <Typography
              variant="subtitle1"
              style={{ fontWeight: "bold", marginBottom: 12 }}
            >
              📋 今後7日間の予約件数
            </Typography>
            <Box
              display="flex"
              flexWrap="wrap"
              style={{ gap: 12, alignItems: "flex-end" }}
            >
              {weekBookings.map((w) => {
                const max = Math.max(...weekBookings.map((x) => x.count), 1);
                const height = (w.count / max) * 80 + 20;
                return (
                  <Box
                    key={w.date}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    style={{ minWidth: 52 }}
                  >
                    <Typography variant="caption" color="textSecondary">
                      {w.count} 件
                    </Typography>
                    <Box
                      style={{
                        width: 32,
                        height,
                        backgroundColor: w.isToday ? "#ed6c02" : "#ffcc80",
                        borderRadius: "4px 4px 0 0",
                        marginTop: 4,
                      }}
                    />
                    <Typography
                      variant="caption"
                      style={{
                        marginTop: 4,
                        fontWeight: w.isToday ? "bold" : "normal",
                      }}
                    >
                      {w.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </CardContent>
        </Card>
        {/* タステックからのお知らせ (3列目) */}
        <Box style={{ flex: "1 1 300px", minWidth: 280, display: "flex" }}>
          <Box style={{ width: "100%" }}>
            <SystemNotice />
          </Box>
        </Box>
      </Box>

      {/* 対応が必要 + 本日のシフト一覧 を横並び */}
      <Box display="flex" flexWrap="wrap" style={{ gap: 16, marginBottom: 16 }}>
        <Card style={{ flex: "1 1 320px", minWidth: 280 }}>
          <CardContent>
            <Typography
              variant="subtitle1"
              style={{ fontWeight: "bold", marginBottom: 12 }}
            >
              🔔 対応が必要
            </Typography>
            <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                padding={1}
                style={{
                  backgroundColor:
                    actionable.castRequest > 0 ? "#fff3e0" : "#f5f5f5",
                  borderRadius: 4,
                }}
              >
                <Typography variant="body2">
                  キャストリクエスト（コスプレ/オプション）
                </Typography>
                <Typography
                  variant="h6"
                  style={{
                    color: actionable.castRequest > 0 ? "#e65100" : "#999",
                    fontWeight: "bold",
                  }}
                >
                  {actionable.castRequest} 件
                </Typography>
              </Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                padding={1}
                style={{
                  backgroundColor:
                    actionable.guestRequest > 0 ? "#fff3e0" : "#f5f5f5",
                  borderRadius: 4,
                }}
              >
                <Typography variant="body2">ゲストリクエスト（新規）</Typography>
                <Typography
                  variant="h6"
                  style={{
                    color: actionable.guestRequest > 0 ? "#e65100" : "#999",
                    fontWeight: "bold",
                  }}
                >
                  {actionable.guestRequest} 件
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Card style={{ flex: "2 1 400px", minWidth: 320 }}>
          <CardContent>
            <Typography
              variant="subtitle1"
              style={{ fontWeight: "bold", marginBottom: 12 }}
            >
              📋 本日のキャストシフト
            </Typography>
            {todayShifts.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                登録されたシフトはありません
              </Typography>
            ) : (
              <Box
                style={{ maxHeight: 240, overflow: "auto" }}
                display="flex"
                flexDirection="column"
              >
                {todayShifts.slice(0, 20).map((s: any) => {
                  const hasActual = !!s.actualWorkStart;
                  return (
                    <Box
                      key={s.castShiftId}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      padding={1}
                      style={{ borderBottom: "1px solid #eee" }}
                    >
                      <Typography variant="body2" style={{ flex: 1 }}>
                        {s.cast?.displayName || "-"}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ flex: 2, fontSize: 12 }}
                      >
                        {s.planWorkStart
                          ? DateTime.fromJSDate(
                              new Date(s.planWorkStart)
                            ).toFormat("HH:mm")
                          : "-"}
                        〜
                        {s.planWorkEnd
                          ? DateTime.fromJSDate(
                              new Date(s.planWorkEnd)
                            ).toFormat("HH:mm")
                          : "-"}
                      </Typography>
                      <Typography
                        variant="caption"
                        style={{
                          color: hasActual ? "#2e7d32" : "#999",
                          fontWeight: "bold",
                        }}
                      >
                        {hasActual ? "✅ 出勤済" : "未出勤"}
                      </Typography>
                    </Box>
                  );
                })}
                {todayShifts.length > 20 && (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ padding: 8, textAlign: "center" }}
                  >
                    ほか {todayShifts.length - 20} 件
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
      <Box margin={2} display="flex" alignItems="center" flexWrap="wrap">
        <Box style={{ marginRight: 16 }}>
          <DayNav
            selectedDate={selectedDate}
            onChange={(d) => setSelectedDate(d)}
            disableFuture
          />
        </Box>
        <Box style={{ marginRight: 16 }}>
          <CsvDateExport
            onDownload={async (s, e) => {
              const blob = await ChangeLogApi.exportCsv(companyId, s, e);
              PageUtils.download(blob, `changeLog_${s}_${e}.csv`);
            }}
            label="変更履歴CSV"
            maxDays={31}
          />
        </Box>
        <Box>
          <InputLabel id="select-multiple-category">カテゴリ</InputLabel>
          <Select
            multiple
            value={selectCategories}
            onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
              setSetSelectCategories((prev: string[]) => {
                if (
                  prev.includes("すべて") &&
                  (event.target.value as string[]).indexOf("すべて") === -1
                ) {
                  return [];
                } else if (
                  !prev.includes("すべて") &&
                  (event.target.value as string[]).indexOf("すべて") !== -1
                ) {
                  return [
                    ...Object.keys(categories).map((key) => String(key)),
                    "すべて",
                  ];
                } else if (
                  (event.target.value as string[]).length ===
                    Object.keys(categories).length &&
                  (event.target.value as string[]).indexOf("すべて") === -1
                ) {
                  return [
                    ...Object.keys(categories).map((key) => String(key)),
                    "すべて",
                  ];
                } else if (
                  (event.target.value as string[]).length <=
                  Object.keys(categories).length
                ) {
                  return (event.target.value as string[]).filter(
                    (name) => name !== "すべて"
                  );
                } else {
                  return event.target.value as string[];
                }
              });
            }}
            input={<Input id="select-multiple-driver" />}
            style={{ width: "200px" }}
            renderValue={(selected) => {
              if ((selected as string[]).includes("すべて")) {
                return "すべて";
              } else {
                return Object.entries(categories)
                  .filter(([key, _]) =>
                    (selected as string[]).includes(String(key))
                  )
                  .map(([_, value]) => value)
                  .join(", ");
              }
            }}
          >
            <MenuItem key={"すべて"} value={"すべて"}>
              <Checkbox
                name="all"
                checked={selectCategories.indexOf("すべて") > -1}
              />
              <ListItemText primary={"すべて"} />
            </MenuItem>
            {Object.entries(categories).map(([key, value]) => (
              <MenuItem key={key} value={String(key)}>
                <Checkbox
                  checked={selectCategories.indexOf(String(key)) !== -1}
                />
                <ListItemText primary={value} />
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Box style={{ marginLeft: "10px" }}>
          <InputLabel id="select-multiple-updatedBy">更新者</InputLabel>
          <Select
            multiple
            value={selectUpdatedBy}
            onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
              setSelectUpdatedBy((prev: string[]) => {
                if (
                  prev.includes("すべて") &&
                  (event.target.value as string[]).indexOf("すべて") === -1
                ) {
                  return [];
                } else if (
                  !prev.includes("すべて") &&
                  (event.target.value as string[]).indexOf("すべて") !== -1
                ) {
                  return [
                    ...[
                      ...staffList.map((staff) => String(staff.staffId)),
                      "すべて",
                    ],
                    "その他",
                  ];
                } else if (
                  (event.target.value as string[]).length ===
                    staffList.length + 1 &&
                  (event.target.value as string[]).indexOf("すべて") === -1
                ) {
                  return [
                    ...[
                      ...staffList.map((staff) => String(staff.staffId)),
                      "すべて",
                    ],
                    "その他",
                  ];
                } else if (
                  (event.target.value as string[]).length <=
                  staffList.length + 1
                ) {
                  return (event.target.value as string[]).filter(
                    (name) => name !== "すべて"
                  );
                } else {
                  return event.target.value as string[];
                }
              });
            }}
            input={<Input id="select-multiple-driver" />}
            style={{ width: "200px" }}
            renderValue={(selected) => {
              if ((selected as string[]).includes("すべて")) {
                return "すべて";
              } else {
                return staffList
                  .filter((staff) =>
                    (selected as string[]).includes(String(staff.staffId))
                  )
                  .map((staff) => staff.name)
                  .join(", ");
              }
            }}
          >
            <MenuItem key={"すべて"} value={"すべて"}>
              <Checkbox
                name="all"
                checked={selectUpdatedBy.indexOf("すべて") > -1}
              />
              <ListItemText primary={"すべて"} />
            </MenuItem>

            {staffList.map((staff) => (
              <MenuItem key={staff.staffId} value={String(staff.staffId)}>
                <Checkbox
                  checked={
                    selectUpdatedBy.indexOf(String(staff.staffId)) !== -1
                  }
                />
                <ListItemText primary={staff.name} />
              </MenuItem>
            ))}
            <MenuItem key={"その他"} value={"その他"}>
              <Checkbox
                name="other"
                checked={selectUpdatedBy.indexOf("その他") > -1}
              />
              <ListItemText primary={"その他"} />
            </MenuItem>
          </Select>
        </Box>
      </Box>
      <Typography>更新履歴</Typography>
      {!filteredChangeLog?.length ? (
        <Box display="flex" flexDirection="column" padding={2}>
          <Typography>更新内容はありません</Typography>
        </Box>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>更新時間</TableCell>
              <TableCell>更新者</TableCell>
              <TableCell>カテゴリ</TableCell>
              <TableCell>内容</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredChangeLog.map((changeLog, index) => {
              if (changeLog instanceof CastReportRes) {
                if (!changeLog.cast) return <></>;
                return (
                  <TableRow key={index}>
                    <TableCell>
                      {DateTimeUtils.toFormatAsLocalTimezone(
                        DateTime.fromJSDate(changeLog.updatedAt).toJSDate(),
                        FORMAT_TYPE.YEAR_DATE_TIME
                      )}
                    </TableCell>
                    <TableCell>{changeLog.cast?.displayName}</TableCell>
                    <TableCell>トラブル報告</TableCell>
                    <TableCell>
                      {changeLog.cast.displayName}さんから 「
                      {EnumUtils.mapToEnum(CastReportType, changeLog.type)}
                      」と通知が来ています。
                    </TableCell>
                  </TableRow>
                );
              }
              if (changeLog instanceof CastShiftRes) {
                if (!changeLog.cast) return <></>;
                return (
                  <TableRow key={index}>
                    <TableCell>
                      {DateTimeUtils.toFormatAsLocalTimezone(
                        DateTime.fromJSDate(changeLog.updatedAt).toJSDate(),
                        FORMAT_TYPE.YEAR_DATE_TIME
                      )}
                    </TableCell>
                    <TableCell>
                      {changeLog.updatedById !== null
                        ? changeLog.updatedBy?.name
                        : changeLog.cast.displayName}
                    </TableCell>
                    <TableCell>キャスト</TableCell>
                    <TableCell>
                      {changeLog.cast.displayName}
                      さん
                      <br />
                      予定シフト：
                      {DateTimeUtils.toFormatAsLocalTimezone(
                        DateTime.fromJSDate(changeLog.planWorkStart).toJSDate(),
                        FORMAT_TYPE.YEAR_DATE_TIME
                      )}
                      ~
                      {changeLog.planWorkEnd
                        ? DateTimeUtils.toFormatAsLocalTimezone(
                            DateTime.fromJSDate(
                              changeLog.planWorkEnd
                            ).toJSDate(),
                            FORMAT_TYPE.YEAR_DATE_TIME
                          )
                        : "未設定"}
                      <br />
                      ステータス：
                      {EnumUtils.mapToEnum(ShiftStatus, changeLog.status)}
                      <br />
                      メモ：
                      {changeLog.castMemo}
                    </TableCell>
                  </TableRow>
                );
              }
              if (changeLog instanceof DriverShiftRes) {
                if (!changeLog.driver) return <></>;
                return (
                  <TableRow key={index}>
                    <TableCell>
                      {DateTimeUtils.toFormatAsLocalTimezone(
                        DateTime.fromJSDate(changeLog.updatedAt).toJSDate(),
                        FORMAT_TYPE.YEAR_DATE_TIME
                      )}
                    </TableCell>
                    <TableCell>
                      {changeLog.updatedById !== null
                        ? changeLog.updatedBy?.name
                        : changeLog.driver.name}
                    </TableCell>
                    <TableCell>ドライバー</TableCell>
                    <TableCell>
                      {changeLog.driver.name}
                      さん
                      <br />
                      予定シフト：
                      {DateTimeUtils.toFormatAsLocalTimezone(
                        DateTime.fromJSDate(changeLog.planWorkStart).toJSDate(),
                        FORMAT_TYPE.YEAR_DATE_TIME
                      )}
                      ~
                      {changeLog.planWorkEnd
                        ? DateTimeUtils.toFormatAsLocalTimezone(
                            DateTime.fromJSDate(
                              changeLog.planWorkEnd
                            ).toJSDate(),
                            FORMAT_TYPE.YEAR_DATE_TIME
                          )
                        : "未設定"}
                      <br />
                      ステータス：
                      {EnumUtils.mapToEnum(ShiftStatus, changeLog.status)}
                      <br />
                      メモ：
                      {changeLog.driverMemo}
                    </TableCell>
                  </TableRow>
                );
              }
              if (changeLog instanceof OrderRes) {
                return (
                  <TableRow key={index}>
                    <TableCell>
                      {DateTimeUtils.toFormatAsLocalTimezone(
                        DateTime.fromJSDate(changeLog.updatedAt).toJSDate(),
                        FORMAT_TYPE.YEAR_DATE_TIME
                      )}
                    </TableCell>
                    <TableCell>{changeLog.updatedBy?.name}</TableCell>
                    <TableCell>受注情報</TableCell>
                    <TableCell>
                      顧客名：{changeLog.guest?.name}
                      様
                      <br />
                      電話番号：{changeLog.guest?.tel}
                      <br />
                      キャスト名：{changeLog.castName?.name}さん
                      <br />
                      予IN：
                      {changeLog.planInTime
                        ? DateTime.fromJSDate(changeLog.planInTime).toFormat(
                            FORMAT_TYPE.YEAR_DATE_TIME
                          )
                        : ""}
                      <br />
                      実IN：
                      {changeLog.actualInTime
                        ? DateTime.fromJSDate(changeLog.actualInTime).toFormat(
                            FORMAT_TYPE.YEAR_DATE_TIME
                          )
                        : ""}
                      <br />
                      コース分数：
                      {changeLog.courseTime}分
                      <br />
                      <Link href={`/order/${changeLog.orderId}`}>
                        受注詳細へ
                      </Link>
                    </TableCell>
                  </TableRow>
                );
              }
            })}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

export default Landing;
